import { useCallback, useEffect, useState } from "react";
import {
  fetchAdminHospitalActsSummaries,
  type AdminHospitalActsSummary,
} from "../api/adminApi";
import { fetchAuditorHospitalActsSummaries } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { hospitalActsSummaries } from "../data";
import type { HospitalActsSummary } from "../types";

function mapSummary(row: AdminHospitalActsSummary): HospitalActsSummary {
  return {
    id: String(row.id),
    hospital: row.hospital,
    actCount: row.act_count,
    providerType: row.provider_type || undefined,
  };
}

function applyHasActsFilter(
  rows: HospitalActsSummary[],
  hasActs: boolean | null | undefined,
): HospitalActsSummary[] {
  if (hasActs === true) return rows.filter((row) => row.actCount > 0);
  if (hasActs === false) return rows.filter((row) => row.actCount === 0);
  return rows;
}

export function useAdminHospitalActs(options?: { hasActs?: boolean | null }) {
  const [summaries, setSummaries] = useState<HospitalActsSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);
  const hasActs = options?.hasActs ?? null;

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setSummaries(applyHasActsFilter(hospitalActsSummaries, hasActs));
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const isAuditor = getStoredAccountType() === "auditor";
      const api = isAuditor
        ? await fetchAuditorHospitalActsSummaries({ has_acts: hasActs })
        : await fetchAdminHospitalActsSummaries({ has_acts: hasActs });
      setSummaries((api.hospitals ?? []).map(mapSummary));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les hôpitaux.");
      setSummaries(applyHasActsFilter(hospitalActsSummaries, hasActs));
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, [hasActs]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    summaries,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
